package one.healthium.log_food.ui.scan_results

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import loco.LOG_FOOD_AMOUNT_FIELD_LABEL
import loco.LOG_FOOD_LOG_TIME_FIELD_LABEL
import loco.LOG_FOOD_SCAN_RESULTS_ADD_BUTTON
import loco.LOG_FOOD_SCAN_RESULTS_DELETE_CANCEL
import loco.LOG_FOOD_SCAN_RESULTS_DELETE_CONFIRM
import loco.LOG_FOOD_SCAN_RESULTS_DELETE_ITEM_TITLE
import one.healthium.loco.resolve
import one.healthium.log_food.ui.scan_results.components.CardShape
import one.healthium.log_food.ui.scan_results.components.DetailsRowInCard
import one.healthium.log_food.ui.scan_results.components.FoodTotalDetails
import one.healthium.log_food.ui.scan_results.components.PickerPanel
import one.healthium.log_food.ui.scan_results.components.PickerSection
import one.healthium.log_food.ui.scan_results.components.ScanResultFoodItemCard
import one.healthium.profile.ui.food.components.FoodDetails
import one.healthium.profile.ui.food.components.formatDate
import one.healthium.profile.ui.food.models.AmountUiModel
import one.healthium.profile.ui.food.models.FoodUiModel
import one.healthium.profile.ui.food.models.FoodUiOptions
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppSwipeToDeleteContainer
import one.healthium.ui.compose.components.cupertino.defaultLocale
import one.healthium.ui.compose.theme.black10
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Instant

interface ScanResultsEventDelegate {
    fun onFoodAdded()
}

@Composable
fun ScanResultsScreen(
    modifier: Modifier = Modifier,
    foods: List<FoodUiModel>,
    eventDelegate: ScanResultsEventDelegate,
) {
    val viewModel = koinViewModel<ScanResultsViewModel>()
    val state by viewModel.state.collectAsState()

    LaunchedEffect(foods) { viewModel.init(foods) }
    EventEffect(viewModel.events) { event ->
        when (event) {
            ScanResultsEvent.FoodAdded -> eventDelegate.onFoodAdded()
        }
    }

    ScanResultsContent(
        modifier = modifier,
        state = state,
        onLogTimeClicked = viewModel::onLogTimeClicked,
        onAmountClicked = viewModel::onAmountClicked,
        onAmountChanged = viewModel::onAmountChanged,
        onTimeChanged = viewModel::onTimeChanged,
        onAddAllClicked = viewModel::onAddAllClicked,
        onDismissPicker = viewModel::onDismissPicker,
        onDeleteItem = viewModel::onDeleteItem,
    )

    AlertUiMessageHandler(viewModel.uiMessage)
}

@Composable
private fun ScanResultsContent(
    modifier: Modifier = Modifier,
    state: ScanResultsUiState,
    onLogTimeClicked: () -> Unit,
    onAmountClicked: (Int) -> Unit,
    onAmountChanged: (AmountUiModel) -> Unit,
    onTimeChanged: (Instant) -> Unit,
    onAddAllClicked: (List<FoodUiOptions>) -> Unit,
    onDismissPicker: () -> Unit,
    onDeleteItem: (Int) -> Unit,
) {
    val locale = defaultLocale()
    val formattedDate = remember(state.selectedDate, locale) {
        formatDate(state.selectedDate, locale)
    }
    val pickerVisible = state.showDatePicker || state.selectedAmountIndex != null

    Box(modifier = modifier.fillMaxSize().testTag("add_food_screen")) {
        // Scrollable content
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // FoodDetails card — always shown
            val singleItemOptions = state.singleItemOptions
            val firstItem = state.foodItems.firstOrNull()
            if (state.isSingleItem && singleItemOptions != null) {
                FoodDetails(
                    option = singleItemOptions,
                    bottomContent = {
                        DetailsRowInCard(
                            title = stringResource(loco.Res.string.LOG_FOOD_LOG_TIME_FIELD_LABEL),
                            value = formattedDate,
                            onClick = { onLogTimeClicked() },
                        )
                        if (firstItem != null) {
                            HorizontalDivider(
                                modifier = Modifier.padding(horizontal = 12.dp),
                                thickness = 1.dp,
                                color = black10,
                            )
                            DetailsRowInCard(
                                title = stringResource(loco.Res.string.LOG_FOOD_AMOUNT_FIELD_LABEL),
                                value = firstItem.amount.displayValue.resolve(),
                                onClick = { onAmountClicked(0) },
                            )
                        }
                    },
                )
            } else if (!state.isSingleItem) {
                FoodTotalDetails(
                    state = state,
                    formattedDate = formattedDate,
                    onLogTimeClicked = onLogTimeClicked,
                )
            }

            Spacer(Modifier.height(12.dp))

            // Individual food item cards — only for multiple items
            if (!state.isSingleItem) {
                val canDelete = state.foodItems.size > 1
                state.foodItems.forEachIndexed { index, item ->
                    if (index > 0) {
                        Spacer(Modifier.height(12.dp))
                    }
                    key(index, item.food.product) {
                        if (canDelete) {
                            AppSwipeToDeleteContainer(
                                modifier = Modifier.padding(horizontal = 12.dp),
                                shape = CardShape,
                                confirmTitle = stringResource(loco.Res.string.LOG_FOOD_SCAN_RESULTS_DELETE_ITEM_TITLE),
                                confirmButton = stringResource(loco.Res.string.LOG_FOOD_SCAN_RESULTS_DELETE_CONFIRM),
                                cancelButton = stringResource(loco.Res.string.LOG_FOOD_SCAN_RESULTS_DELETE_CANCEL),
                                onDelete = { onDeleteItem(index) },
                            ) {
                                ScanResultFoodItemCard(
                                    modifier = Modifier.fillMaxWidth(),
                                    item = item,
                                    onAmountClicked = { onAmountClicked(index) },
                                )
                            }
                        } else {
                            ScanResultFoodItemCard(
                                modifier = Modifier.padding(horizontal = 12.dp),
                                item = item,
                                onAmountClicked = { onAmountClicked(index) },
                            )
                        }
                    }
                }
            }

            // Bottom padding so content doesn't hide behind the overlay
            Spacer(Modifier.height(400.dp))
        }

        // Bottom overlay: Add button + Picker panel
        Column(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            AppProgressButton(
                modifier = Modifier
                    .defaultMinSize(minWidth = 176.dp)
                    .padding(bottom = 24.dp)
                    .testTag("add_food_button"),
                text = stringResource(loco.Res.string.LOG_FOOD_SCAN_RESULTS_ADD_BUTTON),
                loading = state.isLoading,
                onClick = { onAddAllClicked(state.allOptions) },
            )

            PickerPanel(
                visible = pickerVisible,
                onDoneClicked = onDismissPicker,
            ) {
                PickerSection(
                    showDatePicker = state.showDatePicker,
                    selectedDate = state.selectedDate,
                    selectedAmountIndex = state.selectedAmountIndex,
                    selectedAmount = state.selectedAmount,
                    onTimeChanged = { onTimeChanged(it) },
                    onAmountChanged = { onAmountChanged(it) },
                )
            }

            if (!pickerVisible) {
                Spacer(Modifier.navigationBarsPadding())
            }
        }
    }
}
